import { IAPIBaseInfo, APIBaseInfo, EAPIErrorType } from './APIBaseInfo';
import { requestConfig as CONFIG } from './RequestConfig';

const isObject = (args: any): boolean => Object.prototype.toString.call(args) === '[object Object]';

export interface IRequestInit extends RequestInit {
    readonly timeout?: number; // 超时时间设置
    readonly hideProgress?: boolean; // 屏蔽顶部的进度指示条
    readonly hideError?: boolean; // 屏蔽 request.addErrorHandler 的错误处理
    abortController?: AbortController;
}

function requestBase(input: RequestInfo, { timeout, abortController, ...init }: IRequestInit = {}): Promise<Response> {
    const abortCtrl = abortController || (window.AbortController && new AbortController());
    if (abortCtrl) {
        Object.assign(init, {
            signal: abortCtrl.signal,
        });
    }
    init.headers = Object.assign({}, CONFIG.getHeaders(), init.headers);
    if (!init.credentials) {
        Object.assign(init, {
            // credentials: 'include',
            credentials: CONFIG.getCredentials(),
        });
    }
    return new Promise((resolve, reject) => {
        // @ts-ignore
        const _fetch = window._kfcFetch || fetch;
        _fetch(input, init).then(it => resolve(it)).catch(err => reject(err));
        setTimeout(() => {
            abortCtrl && abortCtrl.abort();
            reject({
                errno: 500,
                errmsg: CONFIG.getTimeoutMsg()
            });
        }, timeout || CONFIG.getTimeout());
    });
}

type TMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export enum ERespKey {
    BY_SELF = '__SELF_HANDLER',
    BY_SELF_RESPONSE = '__SELF_RESPONSE'
}

export interface IRequestParams<P = IPlainObject | FormData> {
    readonly url: string;
    readonly respKey?: string | ERespKey;
    readonly method: TMethod;
    readonly params?: P;
    readonly options?: IRequestInit;
}

const CONVERT = {
    toQueryString(params: IPlainObject | FormData) {
        const entries = params instanceof FormData ? Array.from(params.entries()) : Object.entries(params);
        return entries.map(
            ([name, value]) => `${name}=${value == null ? '' : encodeURIComponent(value)}`,
        ).join('&');
    },
};

export function request<R, P = IPlainObject | FormData>(
    // @ts-ignore
    IRP: IRequestParams<P>,
): Promise<R> {
    let { url, method, params = {}, respKey = 'data', options = {} } = IRP;
    const apiInfo = new APIBaseInfo({
        method: method,
        path: url,
    });
    CONFIG.handleSendRequest(apiInfo);

    if (respKey == null) {
        respKey = 'data';
    }
    if (method === 'GET') {
        const qsParamsStr = CONVERT.toQueryString(params);
        if (qsParamsStr.length > 0) {
            url = `${url}${(url.indexOf('?') > -1 ? '&' : '?')}${qsParamsStr}`;
        }
        apiInfo.params = qsParamsStr;
    } else {
        let body: any = params;
        const headers = options.headers || {};
        if (params instanceof FormData) {
            // 不要手动添加 'Content-Type' ，会报错：no multipart boundary was found
            // headers['Content-Type'] = 'multipart/form-data';
        } else {
            headers['Content-Type'] = headers['Content-Type'] || CONFIG.getPostContentType();
            headers['Content-Type'] !== 'text/plain' && (body = JSON.stringify(params));
            headers['Content-Type'] === 'application/json' && (body = params);
            apiInfo.params = body.length > CONFIG.getAPIBaseInfoBodyLength()
                ? body.substring(0, CONFIG.getAPIBaseInfoBodyLength())
                : body;
        }
        Object.assign(options, {
            method: method,
            body: body,
            headers: headers,
        });
    }

    options.referrerPolicy = 'origin';
    options.credentials = 'include';
    options.mode = 'cors';

    /**
     * 处理 COMMON_map
     */
    COMMON_map.forEach((index, val) => {
        if (url.indexOf(val) === 0) {
            url = url.replace(val, index);
        }
    });

    const { hideProgress, hideError, ...opts } = options;
    // !hideProgress && Progress.start();
    return new Promise<R>(async (resolve, reject) => {
        if (!await CONFIG.isSend(IRP)) {
            reject({
                fe_msg: 'ignored: request abandoned',
                fe_no: 20,
            });
            return;
        }
        apiInfo.timingBegin();
        requestBase(CONFIG.getHostName() + url, opts).then(resp => {
            apiInfo.timingEnd();
            // 完全由调用者自行处理 response（可用于 文件保存 等功能）
            if (respKey === ERespKey.BY_SELF_RESPONSE) {
                resolve(resp as any);
                CONFIG.handleSuccessAPIBaseInfo(apiInfo.data);
                // !hideProgress && Progress.done();
                return;
            }
            if (!resp.ok) {
                // Response.ok 只读：包含了一个布尔值，标示该 Response 成功（HTTP 状态码的范围在 200-299）
                if (resp.status === CONFIG.getNeedLoginHTTPStatus()) {
                    CONFIG.handleRedirectLogin(apiInfo);
                    CONFIG.handleLogin();
                    return false;
                } else {
                    reject({
                        errmsg: apiInfo.errmsg = resp.statusText,
                        errno: apiInfo.errno = resp.status,
                    });
                    apiInfo.errType = EAPIErrorType.HTTP;
                    CONFIG.handleErrorAPIBaseInfo(apiInfo);
                }
            }

            resp.json()
                .then(val => {
                    if (!CONFIG.loginCheck(val)) {
                        CONFIG.handleRedirectLogin(apiInfo);
                        return;
                    }
                    if (val.code != null) { // 用于兼容早期的接口，0 表示成功
                        val.errno = val.code;
                    }
                    if (val.status != null) { // 规范的字段名，200 表示成功
                        val.errno = val.status;
                    }
                    if (val.message != null) {
                        val.errmsg = val.message;
                    }
                    // 此字段用于某些自行处理的场景
                    val.SUCCESSCODE =
                        (val.code != null && +val.errno === 0) || (val.status != null && +val.errno === 200);

                    apiInfo.traceid = val.traceId;

                    if (respKey === ERespKey.BY_SELF) {
                        resolve(val);
                        CONFIG.handleSuccessAPIBaseInfo(apiInfo.data);
                    } else if ((val.code != null && +val.errno === 0) || (val.status != null && +val.errno === 200)) {
                        resolve(respKey === '' ? val : val[respKey]);
                        CONFIG.handleSuccessAPIBaseInfo(apiInfo.data);
                    } else {
                        // 接口 errno 不为 0
                        reject({
                            errmsg: apiInfo.errmsg = val.errmsg,
                            errno: apiInfo.errno = val.errno,
                            data: val.data
                        });
                        apiInfo.errType = EAPIErrorType.BUSINESS;
                        CONFIG.handleErrorAPIBaseInfo(apiInfo);
                    }
                })
                .catch(err => {
                    // resp.json() 格式化出错
                    reject({
                        errmsg: apiInfo.errmsg = (isObject(err) ? JSON.stringify(err) : String(err)),
                        errno: apiInfo.errno = 40,
                    });
                    apiInfo.errType = EAPIErrorType.FE;
                    CONFIG.handleErrorAPIBaseInfo(apiInfo);
                });
            // !hideProgress && Progress.done();
            CONFIG.handleRecieveResponse(apiInfo);
        }).catch(err => {
            if (err.name === 'AbortError') {
                return;
            }
            if (err.errno) {
                // 超时错误
                reject(err);
                Object.assign(apiInfo, err);
                apiInfo.errType = EAPIErrorType.BUSINESS;
                CONFIG.handleErrorAPIBaseInfo(apiInfo);
            } else {
                // fetch 运行出错
                reject({
                    errmsg: apiInfo.errmsg = (isObject(err) ? JSON.stringify(err) : String(err)),
                    errno: apiInfo.errno = 40,
                });
                apiInfo.errType = EAPIErrorType.FE;
                CONFIG.handleErrorAPIBaseInfo(apiInfo);
            }
            // !hideProgress && Progress.done();
            CONFIG.handleRecieveResponse(apiInfo);
        });
    }).catch((err) => {
        if (err?.fe_no === 20) { // 前端放弃了本次请求
            return Promise.reject(err);
        }
        !hideError && COMMON_error_handlers.forEach(it => it(err, apiInfo.data));
        return Promise.reject(err);
    });
}

request.CONFIG = CONFIG;

export interface IResponseError {
    readonly errmsg: string;
    readonly errno: number;
    [key: string]: any;
}

export type IResponseErrorHandler = (p: IResponseError, apiInfo: IAPIBaseInfo) => void;

const COMMON_error_handlers: IResponseErrorHandler[] = [];

request.addErrorHandler = (handler: IResponseErrorHandler) => {
    COMMON_error_handlers.push(handler);
};

const COMMON_map: Map<string, string> = new Map();
request.addMap = (key: string, value: string) => {
    COMMON_map.set(key, value);
};

request.get = <P, R>(url: string, params?: P, respKey?: string, options?: IRequestInit): Promise<R> => {
    return request<R, P>({
        method: 'GET',
        url,
        params,
        respKey,
        options,
    });
};
request.post = <P, R>(url: string, params?: P, respKey?: string, options?: IRequestInit): Promise<R> => {
    return request<R, P>({
        method: 'POST',
        url,
        params,
        respKey,
        options,
    });
};
request.put = <P, R>(url: string, params?: P, respKey?: string, options?: IRequestInit): Promise<R> => {
    return request<R, P>({
        method: 'PUT',
        url,
        params,
        respKey,
        options,
    });
};
request.delete = <P, R>(url: string, params?: P, respKey?: string, options?: IRequestInit): Promise<R> => {
    return request<R, P>({
        method: 'DELETE',
        url,
        params,
        respKey,
        options,
    });
};
request.patch = <P, R>(url: string, params?: P, respKey?: string, options?: IRequestInit): Promise<R> => {
    return request<R, P>({
        method: 'PATCH',
        url,
        params,
        respKey,
        options,
    });
};
